'use strict'

const debug = require('debug')('metronome-utils:uniswap')
const pTap = require('p-tap')
const { findTokenBySymbol, findTokenByAddress } = require('token-list')
const { findByChainId } = require('chain-list')
const { fromUnit, handleForkedChainId } = require('./utils')

const uniswapV2Router02Abi = require('./abi/uniswap-v2-router-02.json')
const traderJoeRouterAbi = require('./abi/trader-joe-router.json')

const uniswapV2Router02Addresses = {
  1: '0xd9e1cE17f2641f24aE83637ab66a2cca9C378B9F', // SushiSwap
  3: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
  4: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
  5: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
  69: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
  137: '0x1b02dA8Cb0d097eB8D57A175b88c7D8b47997506', // SushiSwap
  43114: '0x1b02dA8Cb0d097eB8D57A175b88c7D8b47997506' // SushiSwap
}

const createUniswapRouter = function (web3) {
  const chainIdPromise = () => web3.eth.getChainId().then(handleForkedChainId)
  const uniswapV2Router02 = chainId =>
    new web3.eth.Contract(
      uniswapV2Router02Abi,
      uniswapV2Router02Addresses[chainId]
    )

  const fallbackUniswapRoupter = () =>
    new web3.eth.Contract(
      uniswapV2Router02Abi,
      '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D'
    )

  const traderJoeRouter = () =>
    new web3.eth.Contract(
      traderJoeRouterAbi,
      '0x60aE616a2155Ee3d9A68541Ba4544862310933d4'
    )

  // Gets the address of an ERC-20 token by symbol - hacked for VSP
  const getTokenAddressOf = (symbol, chainId) =>
    findTokenBySymbol(symbol, chainId).address

  // Gets the address of an ERC-20 token by symbol - hacked for VSP
  const getTokenDecimalsOf = function (symbol, chainId) {
    const token = findTokenBySymbol(symbol, chainId)
    if (token) {
      return token.decimals
    }
    return 0
  }

  // Gets the amounts of tokens to get in a swap
  const getAmountOut = (amount, tokensPath, defaultBlock) =>
    chainIdPromise().then(chainId =>
      uniswapV2Router02(chainId)
        .methods.getAmountsOut(
          amount,
          tokensPath.map(tokenPath => getTokenAddressOf(tokenPath, chainId))
        )
        .call({}, defaultBlock)
        .catch(function (e) {
          if (chainId === 1) {
            return fallbackUniswapRoupter()
              .methods.getAmountsOut(
                amount,
                tokensPath.map(tokenPath =>
                  getTokenAddressOf(tokenPath, chainId)
                )
              )
              .call({}, defaultBlock)
          }
          if (chainId === 43114) {
            return traderJoeRouter()
              .methods.getAmountsOut(
                amount,
                tokensPath.map(tokenPath =>
                  getTokenAddressOf(tokenPath, chainId)
                )
              )
              .call({}, defaultBlock)
          }
          throw e
        })
        .then(amounts => amounts[amounts.length - 1])
    )

  /**
   * Gets the rate between two tokens.
   *
   * @param {string} fromToken The token symbol whose rate is needed.
   * @param {string} toToken The base token symbol.
   * @param {number} [defaultBlock] The block when the rate has to be queried.
   * @returns {Promise<string>} The output tokens of 1 input token.
   */
  const getTokenRate = function (fromToken, toToken, defaultBlock) {
    debug('Getting %s/%s rate', fromToken, toToken)

    return chainIdPromise()
      .then(function (chainId) {
        // Start by replacing the native tokens so all the calcs below are done
        // using the wrapped tokens.
        const { nativeTokenSymbol, wrappedToken } = findByChainId(chainId)
        const _fromToken =
          fromToken === nativeTokenSymbol ? wrappedToken.symbol : fromToken
        const _toToken =
          toToken === nativeTokenSymbol ? wrappedToken.symbol : toToken

        const decimals = getTokenDecimalsOf(_fromToken, chainId)
        if (decimals === 0) {
          debug(
            'Could not get %s/%s rate: Token not listed in vesper list. Assuming rate is 0.',
            fromToken,
            toToken
          )
          return '0'
        }
        // Generate the string representing 1 unit of the token and one micro
        // unit of the token too.
        const oneUnit = `1${'0'.repeat(decimals)}`
        const oneMicroUnit = `1${'0'.repeat(decimals - 6)}`

        // If there is nothing to do, do nothing!
        if (_fromToken === _toToken) {
          return oneUnit
        }

        // To build the swap path, start with the wrapped token of the chain
        // (WETH, WMATIC, etc). Then, if the origin token is differnet, add it
        // as the first step in the path. Finally, if the destination token is
        // not the wrapped token either, add it to the end of the path.
        //
        // path = [[fromToken,] wrappedToken[, toToken]]
        //
        const path = [wrappedToken.symbol]
        if (_fromToken !== wrappedToken.symbol) {
          path.unshift(_fromToken)
        }
        if (_toToken !== wrappedToken.symbol) {
          path.push(_toToken)
        }
        debug('Trying path: %s', path.join('>'))

        // To prevent slipages due to low liquidity in the swapping pools, get
        // the estimated output of 1 and 1e-6 tokens. Then get the highest
        // value.
        return Promise.all([
          getAmountOut(oneUnit, path, defaultBlock),
          getAmountOut(oneMicroUnit, path, defaultBlock)
        ])
          .catch(function (err) {
            debug(
              'Failed getting %s/%s rate: %s',
              _fromToken,
              _toToken,
              err.message
            )
            // Some tokens may not even have a direct path through the wrapped
            // native token of the blockchain but may be swappable through DAI.
            const newPath = path.map(token =>
              token === wrappedToken.symbol ? 'DAI' : token
            )
            // VSP does not have a direct route to DAI so let's add the wrapped
            // token back...
            if (_fromToken === 'VSP') {
              newPath.splice(1, 0, wrappedToken.symbol)
            }
            if (_toToken === 'VSP') {
              newPath.splice(-1, 0, wrappedToken.symbol)
            }
            // Now remove duplicates just in case...
            const dedupPath = newPath.filter(
              (step, i) => i === 0 || (i > 0 && step !== newPath[i - 1])
            )
            debug('Trying new path: %s', dedupPath.join('>'))

            return Promise.all([
              getAmountOut(oneUnit, dedupPath, defaultBlock),
              getAmountOut(oneMicroUnit, dedupPath, defaultBlock)
            ])
          })
          .then(function ([outForOne, outForOneMicro]) {
            const outForOneMicroEquiv = `${outForOneMicro}000000`
            return BigInt(outForOneMicroEquiv) > BigInt(outForOne)
              ? outForOneMicroEquiv
              : outForOne
          })
          .then(
            pTap(function (rate) {
              debug(
                '%s/%s rate is %s',
                _fromToken,
                _toToken,
                fromUnit(rate, getTokenDecimalsOf(_toToken, chainId))
              )
            })
          )
      })
      .catch(function (err) {
        debug('Could not get %s/%s rate: %s', fromToken, toToken, err.message)
        return '0'
      })
  }

  // Kept for compatibility reasons. Use getTokenRate() instead.
  const getVspRate = (...args) => getTokenRate('VSP', ...args)

  // Swap ETH for tokens.
  const swapExactEthForToken = (path, { amountOutMin, from, gas, value }) =>
    chainIdPromise().then(chainId =>
      uniswapV2Router02(chainId)
        .methods.swapExactETHForTokens(
          amountOutMin,
          path,
          from,
          Math.round(Date.now() / 1000) + 60
        )
        .send({ from, gas, value })
        .catch(function (e) {
          if (chainId === 1) {
            return fallbackUniswapRoupter()
              .methods.swapExactETHForTokens(
                amountOutMin,
                path,
                from,
                Math.round(Date.now() / 1000) + 60
              )
              .send({ from, gas, value })
          }
          if (chainId === 43114) {
            return traderJoeRouter()
              .methods.swapExactAVAXForTokens(
                amountOutMin,
                path,
                from,
                Math.round(Date.now() / 1000) + 60
              )
              .send({ from, gas, value })
          }
          throw e
        })
    )

  // Swap helper. Give native tokens (i.e. ETH) and get other token in exchange.
  const swapEthForTokens = (tokenAddress, { amountOutMin = 1, from, value }) =>
    chainIdPromise().then(function (chainId) {
      const { wrappedToken } = findByChainId(chainId)
      const token = findTokenByAddress(tokenAddress, chainId)
      const dai = findTokenBySymbol('DAI', chainId)

      debug('Swapping %s to %s', wrappedToken.symbol, token.symbol)
      return swapExactEthForToken([wrappedToken.address, tokenAddress], {
        amountOutMin,
        from,
        gas: 200000,
        value
      }).catch(function (err) {
        debug('Swap failed: %s', err.message)

        // Just in case the native-token-to-wanted-token pair does not exist...
        debug('Trying through DAI...')
        return swapExactEthForToken(
          [wrappedToken.address, dai.address, tokenAddress],
          {
            amountOutMin,
            from,
            gas: 300000,
            value
          }
        )
      })
    })

  return {
    getAmountOut,
    getVspRate,
    getTokenRate,
    swapEthForTokens
  }
}

module.exports = createUniswapRouter
